import { FloatingActionBar } from '@dev-spendesk/grapes';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import {
  NotificationType,
  useNotifications,
} from 'modules/app/notifications/hooks/useNotifications';
import type { AppDispatch } from 'modules/app/redux/store';
import { cancelRequest } from 'modules/requests/api/cancelRequest';
import { denyRequest } from 'modules/requests/api/denyRequest';
import { DenyRequestModal } from 'modules/requests/components/common/DenyRequestModal';
import {
  approveRequest,
  removeRequestLocally,
  updateRequestLocally,
  fetchRequestsStats,
  fetchRequestsStatsSuccess,
} from 'modules/requests/redux/legacy/actions';
import { type SubnavigationItem } from 'modules/requests/utils/navigation';
import { companyAPI } from 'src/core/api/axios';
import { appQueryClient } from 'src/core/api/client';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { useRemoveRequestsFromRequestsDraftCache } from 'src/core/modules/requests/api/useRequestsDraftQuery';
import { type ExtendedRequestState } from 'src/core/modules/requests/models/requestState';
import { type RequestType } from 'src/core/modules/requests/models/requestType';
import { getRequestsStats } from 'src/core/modules/requests/redux/legacy/selectors';
import { AnalyticEventName, track } from 'src/core/utils/analytics';
import { getFilenameFromContentDispositionHeader } from 'src/core/utils/contentDisposition';
import { downloadFromBlob } from 'src/core/utils/fileDownloader';
import { logger } from 'src/utils/datadog-log-wrapper';

import { CancelRequestsModal } from './components/CancelRequestsModal';

type Props<
  Request extends {
    id: string;
    state: ExtendedRequestState;
    type: RequestType;
  },
> = {
  selectedRowIds: string[];
  type: SubnavigationItem;
  unselectRequests: () => void;
  requests: Request[];
  isItemEligibleForAction: (request: Request, action: string) => boolean;
};

export const RequestsTableActions = <
  Request extends {
    id: string;
    state: ExtendedRequestState;
    type: RequestType;
  },
>({
  selectedRowIds,
  type,
  unselectRequests,
  requests,
  isItemEligibleForAction,
}: Props<Request>) => {
  const { t } = useTranslation('global');
  const { pushNotif } = useNotifications();

  const companyId = useCompanyId();
  const stats = useSelector(getRequestsStats);

  const removeRequestsFromRequestsDraftCache =
    useRemoveRequestsFromRequestsDraftCache();

  const dispatch = useDispatch<AppDispatch>();
  const [denyRequestContext, setDenyRequestContext] = useState<{
    open: boolean;
    requests: Request[];
  }>({
    open: false,
    requests: [],
  });
  const [deleteRequestContext, setDeleteRequestContext] = useState<{
    open: boolean;
    requests: Request[];
  }>({
    open: false,
    requests: [],
  });

  const dispatchApproveRequest = (request: Request): Promise<void> => {
    return new Promise((resolve) => {
      dispatch(
        approveRequest(request, {
          updateLocally: true,
          callback: () => {
            resolve();
          },
        }),
      );
    });
  };
  const deleteDraftRequest = async (request: Request) => {
    try {
      await companyAPI.delete(`/drafts/${request.id}`, {
        companyId,
      });
      removeRequestsFromRequestsDraftCache([request.id]);
      dispatch(
        fetchRequestsStatsSuccess({
          ...stats,
          drafts: stats.drafts - 1,
        }),
      );
    } catch {
      logger.warn(`[BULK] failed to delete draft request ${request.id}`, {
        scope: 'requests::actions::bulk',
        team: 'travel-and-expenses',
      });
    }
  };
  const bulkAction =
    (function_: (request: Request) => Promise<void>) =>
    async (requestsToBulk: Request[]) => {
      const results = await Promise.allSettled(
        requestsToBulk.map((request) => function_(request)),
      );
      const fulfilledAction = results.filter(
        (result) => result.status === 'fulfilled',
      ).length;

      if (fulfilledAction > 0) {
        unselectRequests();
      }

      pushNotif({
        type: fulfilledAction
          ? NotificationType.Success
          : NotificationType.Danger,
        message: t('requests.actions.successfulWithCount', {
          count: fulfilledAction,
        }),
      });
    };
  const downloadRequests = async (requestsToDownoad: Request[]) => {
    const { data, headers } = await companyAPI.post(
      '/requests/download',
      { requestIds: requestsToDownoad.map((request) => request.id) },
      {
        companyId,
        responseType: 'blob',
      },
    );

    // Due to CORS issue content-disposition is not always passed by the server.
    // So to force the extension to be a csv, hardcode the filename.
    const filename =
      getFilenameFromContentDispositionHeader(headers['content-disposition']) ||
      'requests.csv';

    downloadFromBlob(data, filename);
  };
  const dispatchDenyRequest = (reason: string) => async (request: Request) => {
    try {
      const data = await denyRequest({
        requestId: request.id,
        denyReason: reason,
        companyId,
      });
      dispatch(updateRequestLocally(data));
      appQueryClient.invalidateQueries(['requests', request.id]);
    } catch {
      setDenyRequestContext(() => ({
        open: false,
        requests: [],
      }));
      throw new Error('Failed to deny request');
    }
  };
  const dispatchCancelRequest = async (request: Request) => {
    try {
      await cancelRequest({
        requestId: request.id,
        companyId,
      });
      dispatch(removeRequestLocally(request.id));
    } catch (error) {
      logger.warn(`[BULK] failed to cancel request ${request.id}`, {
        scope: 'requests::actions::bulk',
        team: 'travel-and-expenses',
      });
      throw error;
    }
  };

  const actions = {
    approve: {
      title: (count: number) => t('requests.approveWithCount', { count }),
      run: bulkAction(dispatchApproveRequest),
    },
    cancel: {
      title: (count: number) => t('requests.cancelWithCount', { count }),
      run: (requestsToCancel: Request[]) =>
        setDeleteRequestContext({ open: true, requests: requestsToCancel }),
    },
    delete: {
      title: (count: number) => t('requests.deleteWithCount', { count }),
      run: bulkAction(deleteDraftRequest),
    },
    deny: {
      title: (count: number) => t('requests.denyWithCount', { count }),
      run: (requestsToDeny: Request[]) =>
        setDenyRequestContext({ open: true, requests: requestsToDeny }),
    },
    download: {
      title: (count: number) => t('requests.downloadWithCount', { count }),
      run: downloadRequests,
    },
  };

  const eligibleActions = Object.entries(actions).map(
    ([actionName, properties]) => {
      const eligibles = requests.filter((request) => {
        return (
          selectedRowIds.includes(request.id) &&
          isItemEligibleForAction(request, actionName)
        );
      });

      return { action: actionName, eligibles, properties };
    },
  );

  if (selectedRowIds.length === 0) {
    return null;
  }
  return (
    <>
      <FloatingActionBar
        actions={eligibleActions
          .map(({ action, eligibles, properties }) => {
            if (eligibles.length < 1) {
              return null;
            }
            return {
              text: properties.title(eligibles.length),
              icon: 'circle-check',
              onClick: async () => {
                track(AnalyticEventName.REQUEST_BULK_ACTION, {
                  type: action,
                  number: eligibles.length,
                  tab: type,
                });
                await properties.run(eligibles);
              },
            };
          })
          .filter((action): action is NonNullable<typeof action> =>
            Boolean(action),
          )}
      >
        {t('misc.nSelected', { count: selectedRowIds.length })}
      </FloatingActionBar>
      <DenyRequestModal
        isOpen={denyRequestContext.open}
        onCancel={() => {
          setDenyRequestContext((state) => ({
            open: false,
            requests: state.requests,
          }));
        }}
        onProcessed={async (reason: string) => {
          await bulkAction(dispatchDenyRequest(reason))(
            denyRequestContext.requests,
          );
          setDenyRequestContext(() => ({
            open: false,
            requests: [],
          }));
          dispatch(fetchRequestsStats());
        }}
      />
      <CancelRequestsModal
        isOpen={deleteRequestContext.open}
        onClose={() => {
          setDeleteRequestContext((state) => ({
            open: false,
            requests: state.requests,
          }));
        }}
        onConfirm={async () => {
          await bulkAction(dispatchCancelRequest)(
            deleteRequestContext.requests,
          );
          setDeleteRequestContext(() => ({
            open: false,
            requests: [],
          }));
          dispatch(fetchRequestsStats());
        }}
      />
    </>
  );
};
